import React, { useEffect } from 'react';
import NodeLibrary from './NodeLibrary';
import type { PaletteType } from '../paletteDrop';
import { FlyoutRoot } from './NodeLibraryFlyout.styled';

interface NodeLibraryFlyoutProps {
  open: boolean;
  /** Close the flyout (called from the X button and Esc). */
  onClose: () => void;
  /** Optional click-to-add — propagates a palette type into the canvas's
   *  drop handler. Drag is wired by NodeLibraryItem internally. */
  onSelectPaletteType?: (paletteType: PaletteType) => void;
  dataTestId?: string;
}

/**
 * Slide-in panel that docks flush to the right of the editor's rail
 * (matching the prototype's `.ops-panel` from `OperatorsPanel` / the
 * editor-mode NodeLibrary). It's anchored absolutely to the BaseLayout
 * Sidebar slot (which is `position: relative`), so the panel slides out
 * over the GraphCanvas without affecting the grid template.
 *
 * The flyout owns:
 *   - the slide-in / slide-out animation,
 *   - the Esc-to-close keyboard shortcut,
 *   - the "X" close affordance,
 * and embeds the existing {@link NodeLibrary} for the actual catalog.
 *
 * The panel stays mounted while closed (with `pointer-events: none`) so
 * search / filter state is preserved across toggles, and the open/close
 * animation isn't competing with a remount.
 */
const NodeLibraryFlyout: React.FC<NodeLibraryFlyoutProps> = ({
  open,
  onClose,
  onSelectPaletteType,
  dataTestId = 'node-library-flyout',
}) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <FlyoutRoot
      $open={open}
      role="dialog"
      aria-label="Node Library"
      aria-hidden={!open}
      data-testid={dataTestId}
      data-state={open ? 'open' : 'closed'}
    >
      <NodeLibrary
        onClose={onClose}
        onSelectPaletteType={onSelectPaletteType}
        dataTestId={`${dataTestId}__library`}
      />
    </FlyoutRoot>
  );
};

export default NodeLibraryFlyout;
